import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { auth } from '@/config/firebase';

// In local dev, empty so Vite proxy handles /api/*. In production, set to backend URL at build time.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Search, Eye, RefreshCw, Lock, Unlock, Mail, Building, Trash2, ExternalLink } from "lucide-react";
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { DeleteStartupDialog } from './DeleteStartupDialog';

interface StartupAdminView {
    id: string;
    name: string;
    org_name: string;
    visibility: 'public' | 'private';
    status: string;
    submitted_by: {
        email: string;
        name: string;
    };
    created_at: string;
    analysis_status: string;
}

interface UserAdminView {
    id: string;
    email: string;
    full_name: string;
    role: string;
    org: {
        id: string;
        name: string;
        type: string;
        role: string;
    } | null;
    created_at: string;
}

export interface FeedbackAdminView {
    id: string;
    user_id: string;
    page: string;
    page_url: string | null;
    section: string | null;
    feedback_text: string;
    image_url: string | null;
    status: string;
    created_at: string;
}

interface StartupsResponse {
    data: StartupAdminView[];
    total: number;
    page: number;
    per_page: number;
}

interface UsersResponse {
    data: UserAdminView[];
    total: number;
    page: number;
    per_page: number;
}

export const AdminDashboard = () => {
    const { user, isAdmin } = useAuth();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('startups');

    // Joint state for pagination/search (resets on tab change could be better but keeping simple)
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    // Upgrade Modal State
    const [upgradeUser, setUpgradeUser] = useState<UserAdminView | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<string>('Explorer');
    const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);

    // Feedback Modal State
    const [selectedFeedback, setSelectedFeedback] = useState<FeedbackAdminView | null>(null);
    const [isFeedbackDetailsOpen, setIsFeedbackDetailsOpen] = useState(false);

    // Plans list
    const availablePlans = ['Explorer', 'Professional', 'Institutional', 'Enterprise'];

    const upgradeMutation = useMutation({
        mutationFn: async ({ userId, planName }: { userId: string, planName: string }) => {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken();
            await axios.post(`${API_BASE_URL}/api/admin/users/${userId}/upgrade`,
                { plan_name: planName },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        },
        onSuccess: () => {
            toast({ title: "Upgrade Successful", description: `User upgraded to ${selectedPlan}` });
            setIsUpgradeOpen(false);
            setUpgradeUser(null);
            queryClient.invalidateQueries({ queryKey: ['admin-users-investors'] });
        },
        onError: () => {
            toast({ title: "Error", description: "Failed to upgrade user.", variant: "destructive" });
        }
    });

    const handleOpenUpgrade = (user: UserAdminView) => {
        setUpgradeUser(user);
        setSelectedPlan('Explorer'); // Default or maybe try to guess current? Current is not in list view yet except implicitly
        setIsUpgradeOpen(true);
    }

    // Fetch startpus
    const { data: startupsData, isLoading: isStartupsLoading, isError: isStartupsError, error: startupsError } = useQuery<StartupsResponse>({
        queryKey: ['admin-startups', page, search],
        queryFn: async () => {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken(true);
            const response = await axios.get(`${API_BASE_URL}/api/admin/startups`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { page, search }
            });
            return response.data;
        },
        enabled: !!user && isAdmin && activeTab === 'startups',
    });

    // Fetch founders
    const { data: foundersData, isLoading: isFoundersLoading, isError: isFoundersError } = useQuery<UsersResponse>({
        queryKey: ['admin-users-founders', page, search],
        queryFn: async () => {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken(true);
            const response = await axios.get(`${API_BASE_URL}/api/admin/users`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { page, search, role: 'founder' }
            });
            return response.data;
        },
        enabled: !!user && isAdmin && activeTab === 'founders',
    });

    // Fetch investors
    const { data: investorsData, isLoading: isInvestorsLoading, isError: isInvestorsError } = useQuery<UsersResponse>({
        queryKey: ['admin-users-investors', page, search],
        queryFn: async () => {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken(true);
            const response = await axios.get(`${API_BASE_URL}/api/admin/users`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { page, search, role: 'investor' }
            });
            return response.data;
        },
        enabled: !!user && isAdmin && activeTab === 'investors',
    });

    // Fetch feedback
    const { data: feedbackData, isLoading: isFeedbackLoading, isError: isFeedbackError, error: feedbackError } = useQuery<FeedbackAdminView[]>({
        queryKey: ['admin-feedback'],
        queryFn: async () => {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken(true);
            const response = await axios.get(`${API_BASE_URL}/api/feedback/admin`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        },
        enabled: !!user && isAdmin && activeTab === 'feedback',
    });

    // Toggle Visibility Mutation
    const toggleVisibilityMutation = useMutation({
        mutationFn: async ({ id, visibility }: { id: string, visibility: 'public' | 'private' }) => {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken();
            await axios.patch(`${API_BASE_URL}/api/admin/startups/${id}/visibility`,
                { visibility },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-startups'] });
            toast({ title: "Visibility updated", description: "Startup visibility has been changed." });
        },
        onError: (error) => {
            toast({ title: "Error", description: "Failed to update visibility.", variant: "destructive" });
        }
    });

    // Re-analyze Mutation
    const reanalyzeMutation = useMutation({
        mutationFn: async (id: string) => {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken();
            await axios.post(`${API_BASE_URL}/api/admin/startups/${id}/reanalyze`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-startups'] });
            toast({ title: "Analysis triggered", description: "Re-analysis has been started in background." });
        },
        onError: (error) => {
            toast({ title: "Error", description: "Failed to trigger analysis.", variant: "destructive" });
        }
    });

    // Delete Dialog State
    const [deleteStartupIds, setDeleteStartupIds] = useState<string[]>([]);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    // Selection State
    const [selectedStartups, setSelectedStartups] = useState<string[]>([]);

    const handleDeleteClick = (id: string) => {
        setDeleteStartupIds([id]);
        setIsDeleteOpen(true);
    };

    const handleBulkDeleteClick = () => {
        if (selectedStartups.length === 0) return;
        setDeleteStartupIds(selectedStartups);
        setIsDeleteOpen(true);
    };

    // Delete User Dialog State
    const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
    const [isDeleteUserOpen, setIsDeleteUserOpen] = useState(false);

    const deleteUserMutation = useMutation({
        mutationFn: async (id: string) => {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const token = await currentUser.getIdToken();
            await axios.delete(`${API_BASE_URL}/api/admin/users/${id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-users-founders'] });
            queryClient.invalidateQueries({ queryKey: ['admin-users-investors'] });
            toast({ title: "User deleted", description: "The user has been deleted successfully." });
            setIsDeleteUserOpen(false);
            setDeleteUserId(null);
        },
        onError: (error) => {
            toast({ title: "Error", description: "Failed to delete user.", variant: "destructive" });
        }
    });

    const handleDeleteUserClick = (id: string) => {
        setDeleteUserId(id);
        setIsDeleteUserOpen(true);
    };

    const handleDeleteSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ['admin-startups'] });
        setSelectedStartups([]); // Clear selection after delete
    };

    const toggleStartupSelection = (id: string) => {
        setSelectedStartups(prev =>
            prev.includes(id)
                ? prev.filter(sid => sid !== id)
                : [...prev, id]
        );
    };

    const toggleAllStartups = () => {
        if (!startupsData?.data) return;
        if (selectedStartups.length === startupsData.data.length) {
            setSelectedStartups([]);
        } else {
            setSelectedStartups(startupsData.data.map(s => s.id));
        }
    };

    if (!isAdmin) {
        return <div className="p-8 text-center">Access Denied</div>;
    }

    const UserTable = ({ data, isLoading, type }: { data?: UsersResponse, isLoading: boolean, type: string }) => (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading ? (
                        <TableRow>
                            <TableCell colSpan={5} className="text-center h-24">
                                <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                            </TableCell>
                        </TableRow>
                    ) : data?.data.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={5} className="text-center h-24">No {type} found.</TableCell>
                        </TableRow>
                    ) : (
                        data?.data.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>
                                    <div className="flex flex-col">
                                        <span className="font-medium">{user.full_name || 'No Name'}</span>
                                        <div className="flex items-center text-xs text-muted-foreground">
                                            <Mail className="w-3 h-3 mr-1" />
                                            {user.email}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {user.org ? (
                                        <div className="flex flex-col">
                                            <span className="font-medium">{user.org.name}</span>
                                            <span className="text-xs text-muted-foreground capitalize">{user.org.role}</span>
                                        </div>
                                    ) : (
                                        <span className="text-muted-foreground">-</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Badge variant="outline" className="capitalize">{user.role}</Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                    {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8"
                                            disabled={user.role !== 'investor'}
                                            onClick={() => handleOpenUpgrade(user)}
                                        >
                                            <RefreshCw className="w-3 h-3 mr-2" />
                                            Upgrade
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                                            onClick={() => handleDeleteUserClick(user.id)}
                                            title="Delete User"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );

    return (
        <div className="container mx-auto py-8 space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
                    <p className="text-muted-foreground mt-2">Manage all system startups, users and analyses.</p>
                </div>
                {activeTab === 'startups' && selectedStartups.length > 0 && (
                    <Button
                        variant="destructive"
                        onClick={handleBulkDeleteClick}
                        className="animate-in fade-in zoom-in duration-200"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Selected ({selectedStartups.length})
                    </Button>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList>
                    <TabsTrigger value="startups">All Startups</TabsTrigger>
                    <TabsTrigger value="founders">Founders</TabsTrigger>
                    <TabsTrigger value="investors">Investors</TabsTrigger>
                    <TabsTrigger value="feedback">Feedback</TabsTrigger>
                </TabsList>

                <div className="flex justify-between items-center py-4">
                    <div className="relative w-64">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search..."
                            className="pl-8"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <TabsContent value="startups">
                    <Card>
                        <CardHeader>
                            <CardTitle>Startups Registry</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {isStartupsLoading ? (
                                <div className="flex justify-center p-8">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            ) : isStartupsError ? (
                                <div className="text-center text-red-500">
                                    <p>Failed to load startups</p>
                                    <p className="text-sm mt-2 text-red-400">{(startupsError as any)?.message || 'Unknown error'}</p>
                                </div>
                            ) : (
                                <div className="rounded-md border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-[50px]">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                        checked={(startupsData?.data?.length ?? 0) > 0 && selectedStartups.length === (startupsData?.data?.length ?? 0)}
                                                        onChange={toggleAllStartups}
                                                    />
                                                </TableHead>
                                                <TableHead>Startup</TableHead>
                                                <TableHead>Owner Org</TableHead>
                                                <TableHead>Visibility</TableHead>
                                                <TableHead>Submitted By</TableHead>
                                                <TableHead>Analysis</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {startupsData?.data.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} className="text-center h-24">No startups found.</TableCell>
                                                </TableRow>
                                            ) : (
                                                startupsData?.data.map((startup) => (
                                                    <TableRow key={startup.id} data-state={selectedStartups.includes(startup.id) ? "selected" : undefined}>
                                                        <TableCell>
                                                            <input
                                                                type="checkbox"
                                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                                checked={selectedStartups.includes(startup.id)}
                                                                onChange={() => toggleStartupSelection(startup.id)}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            <span className="text-base">{startup.name}</span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                {startup.org_name ? (
                                                                    <Badge variant="outline">{startup.org_name}</Badge>
                                                                ) : (
                                                                    <span className="text-muted-foreground text-sm">-</span>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                {startup.visibility === 'public' ? (
                                                                    <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-100">Public</Badge>
                                                                ) : (
                                                                    <Badge variant="secondary" className="bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100">Private</Badge>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-col text-sm">
                                                                <span>{startup.submitted_by.name || 'Unknown'}</span>
                                                                <span className="text-xs text-muted-foreground">{startup.submitted_by.email}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant={startup.analysis_status === 'completed' ? 'default' : 'secondary'}>
                                                                {startup.analysis_status || 'None'}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-muted-foreground text-sm">
                                                            {startup.created_at ? format(new Date(startup.created_at), 'MMM d, yyyy') : '-'}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <div className="flex justify-end gap-2">
                                                                <Button
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    asChild
                                                                    title="View Details"
                                                                >
                                                                    <Link to={`/startups/${startup.id}`}>
                                                                        <Eye className="h-4 w-4" />
                                                                    </Link>
                                                                </Button>

                                                                <Button
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    onClick={() => reanalyzeMutation.mutate(startup.id)}
                                                                    disabled={reanalyzeMutation.isPending}
                                                                    title="Trigger Re-analysis"
                                                                >
                                                                    {reanalyzeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                                                                </Button>

                                                                <Button
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    onClick={() => toggleVisibilityMutation.mutate({
                                                                        id: startup.id,
                                                                        visibility: startup.visibility === 'public' ? 'private' : 'public'
                                                                    })}
                                                                    disabled={toggleVisibilityMutation.isPending}
                                                                    title={startup.visibility === 'public' ? "Make Private" : "Make Public"}
                                                                >
                                                                    {toggleVisibilityMutation.isPending ? (
                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                    ) : (
                                                                        startup.visibility === 'public' ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />
                                                                    )}
                                                                </Button>

                                                                <Button
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                    onClick={() => handleDeleteClick(startup.id)}
                                                                    title="Hard Delete"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="founders">
                    <Card>
                        <CardHeader>
                            <CardTitle>Founders Directory</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <UserTable data={foundersData} isLoading={isFoundersLoading} type="founders" />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="investors">
                    <Card>
                        <CardHeader>
                            <CardTitle>Investors Directory</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <UserTable data={investorsData} isLoading={isInvestorsLoading} type="investors" />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="feedback">
                    <Card>
                        <CardHeader>
                            <CardTitle>User Feedback</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {isFeedbackLoading ? (
                                <div className="flex justify-center p-8">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            ) : isFeedbackError ? (
                                <div className="text-center text-red-500 p-8">
                                    <p>Failed to load feedback</p>
                                    <p className="text-sm mt-2 text-red-400">{(feedbackError as any)?.message || 'Unknown error'}</p>
                                </div>
                            ) : (
                                <div className="rounded-md border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Page</TableHead>
                                                <TableHead>Section</TableHead>
                                                <TableHead>Feedback</TableHead>
                                                <TableHead>Attachment</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {!feedbackData || feedbackData.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} className="text-center h-24">No feedback found.</TableCell>
                                                </TableRow>
                                            ) : (
                                                feedbackData.map((feedback) => (
                                                    <TableRow key={feedback.id} onClick={() => { setSelectedFeedback(feedback); setIsFeedbackDetailsOpen(true); }} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                        <TableCell className="whitespace-nowrap w-[150px]" onClick={(e) => e.stopPropagation()}>
                                                            {new Date(feedback.created_at).toLocaleDateString()}
                                                            <div className="text-xs text-muted-foreground">{new Date(feedback.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                        </TableCell>
                                                        <TableCell className="max-w-[150px] truncate" onClick={(e) => e.stopPropagation()}>
                                                            {feedback.page_url ? (
                                                                <a href={feedback.page_url} target="_blank" rel="noopener noreferrer" className="hover:underline text-indigo-600 dark:text-indigo-400 font-medium">
                                                                    <Badge variant="outline">{feedback.page}</Badge>
                                                                </a>
                                                            ) : (
                                                                <Badge variant="outline">{feedback.page}</Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="max-w-[150px] truncate" onClick={(e) => e.stopPropagation()}>
                                                            {feedback.section ? <span className="text-sm text-muted-foreground">{feedback.section}</span> : '-'}
                                                        </TableCell>
                                                        <TableCell className="max-w-[400px]">
                                                            <p className="text-sm line-clamp-2" title={feedback.feedback_text}>
                                                                {feedback.feedback_text}
                                                            </p>
                                                        </TableCell>
                                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                                            {feedback.image_url ? (
                                                                <div className="flex items-center gap-2">
                                                                    <a
                                                                        href={feedback.image_url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-xs text-blue-500 hover:text-blue-700 hover:underline flex items-center gap-1"
                                                                    >
                                                                        <ExternalLink className="w-3 h-3" />
                                                                        View
                                                                    </a>
                                                                </div>
                                                            ) : (
                                                                <span className="text-xs text-muted-foreground">-</span>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <Dialog open={isUpgradeOpen} onOpenChange={setIsUpgradeOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Upgrade User Plan</DialogTitle>
                        <DialogDescription>
                            Change subscription plan for {upgradeUser?.full_name} ({upgradeUser?.email}).
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="plan" className="text-right">
                                Plan
                            </Label>
                            <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                                <SelectTrigger className="col-span-3">
                                    <SelectValue placeholder="Select a plan" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availablePlans.map((p) => (
                                        <SelectItem key={p} value={p}>{p}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsUpgradeOpen(false)}>Cancel</Button>
                        <Button
                            onClick={() => upgradeUser && upgradeMutation.mutate({ userId: upgradeUser.id, planName: selectedPlan })}
                            disabled={upgradeMutation.isPending}
                        >
                            {upgradeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Upgrade
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DeleteStartupDialog
                startupIds={deleteStartupIds}
                open={isDeleteOpen}
                onOpenChange={setIsDeleteOpen}
                onSuccess={handleDeleteSuccess}
            />

            <Dialog open={isDeleteUserOpen} onOpenChange={setIsDeleteUserOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete User</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this user? This action cannot be undone and will remove them from both Auth and Database.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteUserOpen(false)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={() => deleteUserId && deleteUserMutation.mutate(deleteUserId)}
                            disabled={deleteUserMutation.isPending}
                        >
                            {deleteUserMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={isFeedbackDetailsOpen} onOpenChange={setIsFeedbackDetailsOpen}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>Feedback Details</DialogTitle>
                        <DialogDescription>Full description of user feedback</DialogDescription>
                    </DialogHeader>
                    {selectedFeedback && (
                        <div className="space-y-4 py-4">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="text-sm font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 px-2.5 py-1 rounded-md">
                                    Page: {selectedFeedback.page}
                                </span>
                                {selectedFeedback.page_url && (
                                    <a href={selectedFeedback.page_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-300 px-2.5 py-1 rounded-md hover:underline hover:bg-indigo-100 flex items-center gap-1">
                                        <ExternalLink className="w-3.5 h-3.5" /> Source Link
                                    </a>
                                )}
                                {selectedFeedback.section && (
                                    <span className="text-sm font-medium text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-300 px-2.5 py-1 rounded-md">
                                        Section: {selectedFeedback.section}
                                    </span>
                                )}
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-md border border-slate-200 dark:border-slate-800 whitespace-pre-wrap text-sm">
                                {selectedFeedback.feedback_text}
                            </div>

                            {selectedFeedback.image_url && (
                                <div className="mt-4 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-900">
                                    <a href={selectedFeedback.image_url} target="_blank" rel="noopener noreferrer" className="block p-1 text-center text-xs text-muted-foreground hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
                                        Click to open full size attached image
                                    </a>
                                    <img src={selectedFeedback.image_url} alt="Feedback Attachment" className="w-full max-h-[300px] object-contain" />
                                </div>
                            )}
                            <div className="text-xs text-muted-foreground pt-4 border-t border-slate-100 dark:border-slate-800">
                                Submitted on: {new Date(selectedFeedback.created_at).toLocaleString()}
                                {selectedFeedback.user_id && ` • User ID: ${selectedFeedback.user_id}`}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
